/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { Route, Routes } from '@angular/router';
import { isDevMode, Type } from '@angular/core';
import { FilesComponent, SearchResultsComponent } from '@alfresco/aca-content';
import { algAdminGuard, AlgFilesComponent, AlgSearchResultsComponent, AlgVirtualFilesComponent } from '@alg/alg-extensions';

/**
 * Substitution de composants routes d'ACA par les notres.
 *
 * Les composants routes ne passent pas par le registre `setComponents()` du systeme
 * d'extensions : on ne peut donc pas les surcharger par identifiant. En revanche
 * `CONTENT_LAYOUT_ROUTES` est un simple objet exporte par ACA, et c'est
 * `app/src/app/app.module.ts` — un fichier qui NOUS appartient — qui l'enregistre.
 * On le reecrit donc au passage, sans toucher a `projects/aca-content`.
 *
 * La substitution se fait **par reference de composant**, pas par chemin d'URL :
 * `FilesComponent` sert 4 routes en ACA 4.3.0 (personal-files, contenu de bibliotheque, ...)
 * et si upstream en ajoute une, elle sera couverte automatiquement. Comparer sur
 * `component.name` serait casse par la minification en production.
 *
 * Voir docs/fork/README.md §8.
 */
const COMPONENT_SUBSTITUTIONS = new Map<Type<any>, Type<any>>([
  // Explorateur type Windows : arbre + liste + previsualisation en volets redimensionnables.
  [FilesComponent, AlgFilesComponent],
  // Page de recherche avec le meme volet de previsualisation.
  [SearchResultsComponent, AlgSearchResultsComponent]
]);

const VIRTUAL_VIEW_PATHS = new Set(['favorites', 'recent-files', 'shared', 'trashcan']);

/**
 * Nombre de substitutions realisees au dernier appel.
 *
 * Sert de garde-fou : si une montee de version d'ACA renomme ou deplace un composant route,
 * la substitution devient silencieusement un no-op et l'utilisateur retrouve l'ecran d'ACA sans
 * que rien n'echoue. `scripts/check-overrides.js` verifie ce compteur.
 */
export let algRouteSubstitutionCount = 0;

/** Nombre de substitutions attendu en ACA 4.3.0 : 4 routes FilesComponent + 1 SearchResultsComponent. */
export const ALG_EXPECTED_ROUTE_SUBSTITUTIONS = 9;

function substitute(route: Route, prefix = ''): Route {
  const next: Route = { ...route };
  const fullPath = [prefix, next.path].filter((part) => part !== undefined && part !== '').join('/');

  if (next.path === 'repository' || next.path === 'repository/:folderId') {
    next.canActivate = [...(next.canActivate ?? []), algAdminGuard];
  }

  if (next.component && COMPONENT_SUBSTITUTIONS.has(next.component)) {
    next.component = COMPONENT_SUBSTITUTIONS.get(next.component);
    algRouteSubstitutionCount++;
  } else if (next.component && VIRTUAL_VIEW_PATHS.has(fullPath)) {
    // Ces composants ne sont pas exportes par le point d'entree public d'aca-content. Le chemin
    // fonctionnel est stable et evite de dependre du nom de classe, susceptible d'etre minifie.
    next.component = AlgVirtualFilesComponent;
    algRouteSubstitutionCount++;
  }

  if (next.children) {
    next.children = next.children.map((child) => substitute(child, fullPath));
  }

  return next;
}

/**
 * Applique nos substitutions a un arbre de routes ACA, sans le muter.
 */
export function withAlgComponents(routes: Route): Route;
export function withAlgComponents(routes: Routes): Routes;
export function withAlgComponents(routes: Route | Routes): Route | Routes {
  algRouteSubstitutionCount = 0;
  const result = Array.isArray(routes) ? routes.map((route) => substitute(route)) : substitute(routes);

  if (isDevMode()) {
    // Expose le resultat pour scripts/check-overrides.js, qui verifie sans authentification
    // que les routes remises au Router pointent bien sur nos composants.
    (window as any).__algRoutes = {
      substitutions: algRouteSubstitutionCount,
      expected: ALG_EXPECTED_ROUTE_SUBSTITUTIONS,
      byPath: flatten(result)
    };
  }

  return result;
}

/** Aplatit l'arbre en `chemin -> nom du composant`, pour inspection depuis le navigateur. */
function flatten(routes: Route | Routes, prefix = ''): Record<string, string> {
  const out: Record<string, string> = {};
  const list = Array.isArray(routes) ? routes : [routes];

  for (const route of list) {
    const full = [prefix, route.path].filter((p) => p !== undefined && p !== '').join('/');
    if (route.component) {
      out[full || '/'] = route.component.name;
    }
    if (route.children) {
      Object.assign(out, flatten(route.children, full));
    }
  }

  return out;
}
